import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import StickyFooter from '../components/StickyFooter';
import Header from '../components/Header';
import CardFeaturesWidget from '../components/widgets/CardFeaturesWidget';
import CardPreview from '../components/CardPreview';
import { getUserCards } from '../utils/api';

import {
  Box,
  Typography,
  Button,
  Paper,
  useTheme,
} from '@mui/material';

import CreditCardIcon from '@mui/icons-material/CreditCard';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

// ✅ Fixed Stat Card
const CardStat = ({ icon, label, value, bgColor, iconColor = "#fff" }) => (
  <Paper
    elevation={1}
    sx={{
      p: 2,
      borderRadius: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      height: "100%",       // ✅ equal height across all cards
      width: "100%",        // ✅ fills full wrapper Box
      boxSizing: "border-box",
    }}
  >
    {/* Icon */}
    <Box
      sx={{
        bgcolor: bgColor,
        width: 44,
        height: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        mr: 2,
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { sx: { color: iconColor } })}
    </Box>

    {/* Text */}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {label && (
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={500}
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {label}
        </Typography>
      )}
      <Typography
        variant="h6"
        fontWeight={600}
        sx={{ lineHeight: 1.4 }}
      >
        {value}
      </Typography>
    </Box>
  </Paper>
);

const Cards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const res = await getUserCards();
        setCards(res.data || []);
      } catch (err) {
        console.error("Error fetching cards:", err);
        setCards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCards();
  }, []);

  // ✅ Stats
  const activeCount = cards.filter(c => c.status === "active").length;
  const pendingCount = cards.filter(c => c.status === "pending").length;
  const totalBalance = cards.reduce((sum, c) => sum + (parseFloat(c.balance) || 0), 0);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        bgcolor: 'background.default',
        color: 'text.primary',
        overflowX: 'hidden',
      }}
    >
      <Box p={{ xs: 2, md: 4 }}>
        <Header onMenuClick={() => {}} mode={isDark ? 'dark' : 'light'} />

        {/* 🧭 Title */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={6}>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1.3} sx={{ fontSize: 13 }}>
              Dashboard &gt; Cards
            </Typography>
            <Typography variant="h6" fontWeight={400} sx={{ fontSize: '1rem', mt: 2 }}>
              Virtual Cards
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/card-application"
            sx={{
              borderRadius: 2,
              mt: 4,
              mr: 3,
              fontSize: '0.8rem',
              ml: 1,
              textTransform: 'none',
              fontWeight: 400,
            }}
          >
            + Apply for Card
          </Button>
        </Box>

        {/* 📊 Stats */}
        <Box display="flex" flexWrap="wrap" gap={3} mt={1} mb={2}>
          <Box flex="0 0 290px">
            <CardStat
              icon={<CreditCardIcon />}
              label="Active Cards"
              value={activeCount}
              bgColor="#38bdf8"
              iconColor="#fff"
            />
          </Box>

          <Box flex="0 0 290px">
            <CardStat
              icon={<HourglassEmptyIcon />}
              label="Pending Applications"
              value={pendingCount}
              bgColor="#e0f2fe"
              iconColor="#1d4ed8"
            />
          </Box>

          <Box flex="0 0 290px">
            <CardStat
              icon={<AccountBalanceWalletIcon />}
              label="Total Card Balance"
              value={`$${totalBalance.toFixed(2)}`}
              bgColor="#d1fae5"
              iconColor="#059669"
            />
          </Box>
        </Box>

        {/* 🃏 Cards Section */}
        <Box mt={4}>
          <Typography variant="h6" fontWeight={500} mb={2}>
            My Cards
          </Typography>

          {loading ? (
            <Typography>Loading...</Typography>
          ) : cards.length === 0 ? (
            <CardFeaturesWidget />
          ) : (
            <Box display="flex" flexWrap="wrap" gap={3}>
              {cards.map((card, idx) => (
                <Box key={idx} flex="0 0 320px">
                  <CardPreview card={card} />
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box mt={4}>
          <StickyFooter />
        </Box>
      </Box>
    </Box>
  );
};

export default Cards;